import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useClienteAuth } from "@/contexts/ClienteAuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileText, Upload, Search, ArrowRightLeft, Download, Trash2, Loader2, HardDrive } from "lucide-react";
import { toast } from "sonner";

const TIPO_DOC = {
  mantencion_preventiva: "Mantención preventiva",
  mantencion_correctiva: "Mantención correctiva",
  documento_normativo: "Documento normativo",
  factura_compra: "Factura de compra",
  manual_equipo: "Manual del equipo",
  certificado: "Certificado",
  otro: "Otro",
};

export default function ClienteDocumentos() {
  const navigate = useNavigate();
  const { api } = useClienteAuth();
  const [docs, setDocs] = useState(null); // null = cargando
  const [equipos, setEquipos] = useState([]);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [upl, setUpl] = useState(null); // {file, equipo_id, tipo_documento, descripcion, fecha_documento}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/api/portal/documentos").then((r) => setDocs(r.data)).catch(() => setDocs([]));
    api.get("/api/portal/equipos").then((r) => setEquipos(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const etiqueta = (e) => `${e.tipo_equipo || ""} ${e.marca || ""} ${e.numero_box ? `(${e.numero_box})` : ""}`.trim();

  const pickFile = (f) => {
    if (!f) return;
    if (equipos.length === 0) {
      toast.error("Para cargar un documento primero debes tener un equipo creado.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) return toast.error("Máximo 10MB por documento");
    setUpl({ file: f, equipo_id: "", tipo_documento: "", descripcion: "", fecha_documento: "" });
  };

  const subir = async () => {
    if (!upl.equipo_id) return toast.error("Elige a qué equipo pertenece el documento");
    if (!upl.tipo_documento) return toast.error("Indica qué tipo de documento es");
    setBusy(true);
    const t = toast.loading("Subiendo y comprimiendo…");
    try {
      const fd = new FormData();
      fd.append("file", upl.file);
      fd.append("tipo_documento", upl.tipo_documento);
      fd.append("descripcion", upl.descripcion);
      fd.append("fecha_documento", upl.fecha_documento);
      await api.post(`/api/portal/equipos/${upl.equipo_id}/documentos`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.dismiss(t); toast.success("Documento guardado");
      setUpl(null); load();
    } catch (e) { toast.dismiss(t); toast.error(e.response?.data?.detail || "Error al subir"); }
    finally { setBusy(false); }
  };

  const mover = async (d, haciaId) => {
    if (!haciaId || haciaId === d.equipo_id) return;
    try {
      await api.post(`/api/portal/documentos/${d.id}/mover`, { desde_equipo_id: d.equipo_id, hacia_equipo_id: haciaId });
      toast.success("Documento trasladado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al trasladar"); }
  };

  const ver = async (d) => {
    try {
      const r = await api.get(`/api/portal/equipos/${d.equipo_id}/documentos/${d.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("No se pudo abrir"); }
  };

  const eliminar = async (d) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/api/portal/equipos/${d.equipo_id}/documentos/${d.id}`);
      toast.success("Eliminado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const filtrados = (docs || []).filter((x) =>
    (tipo === "todos" || x.tipo_documento === tipo) &&
    (!q || `${x.equipo} ${x.descripcion} ${x.nombre_original} ${TIPO_DOC[x.tipo_documento] || ""}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="h-5 w-5 text-[#384091] flex-shrink-0" />
            <span className="text-sm font-semibold text-[#384091] truncate">Mis Documentos</span>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.docx,image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickFile(f); }} />
            <Button size="sm" className="bg-[#384091] hover:bg-[#2D3375] text-white"
              onClick={() => fileRef.current?.click()}>
              <Upload className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Cargar documento</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/cliente/dashboard")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {upl && (
          <Card className="border-0 shadow-md ring-2 ring-[#384091]/30">
            <CardContent className="p-5 space-y-3">
              <p className="text-sm font-medium text-gray-700 truncate">📄 {upl.file.name}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">¿A qué equipo pertenece? *</Label>
                  <Select value={upl.equipo_id} onValueChange={(v) => setUpl((p) => ({ ...p, equipo_id: v }))}>
                    <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="Seleccionar equipo" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {equipos.map((e) => <SelectItem key={e.id} value={e.id}>{etiqueta(e)}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs">¿Qué tipo de documento es? *</Label>
                  <Select value={upl.tipo_documento} onValueChange={(v) => setUpl((p) => ({ ...p, tipo_documento: v }))}>
                    <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(TIPO_DOC).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Fecha del documento</Label>
                  <Input type="date" value={upl.fecha_documento}
                    onChange={(e) => setUpl((p) => ({ ...p, fecha_documento: e.target.value }))}
                    className="h-10 bg-white no-uppercase" /></div>
                <div><Label className="text-xs">Descripción breve</Label>
                  <Input value={upl.descripcion} onChange={(e) => setUpl((p) => ({ ...p, descripcion: e.target.value }))}
                    className="h-10 bg-white" placeholder="Ej: MP realizada por Odontofix" /></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-[#384091] text-white" onClick={subir} disabled={busy}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar documento"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setUpl(null)}>Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Búsqueda y filtros */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por palabra: equipo, descripción, nombre del archivo…" className="pl-9 h-11 bg-white" />
          </div>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="h-11 bg-white sm:w-60"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">Todos los tipos</SelectItem>
              {Object.entries(TIPO_DOC).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {docs === null ? (
          <p className="text-center text-gray-400 py-14">Cargando…</p>
        ) : docs.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-10 text-center space-y-4">
              <FileText className="h-10 w-10 text-gray-300 mx-auto" />
              <p className="text-gray-500">Aún no tienes documentos.</p>
              {equipos.length === 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-amber-700 bg-amber-50 rounded-xl px-4 py-3 inline-block">
                    Para cargar un documento primero debes tener un <b>equipo creado</b>.
                  </p>
                  <div>
                    <Button className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white"
                      onClick={() => navigate("/cliente/dashboard")}>
                      <HardDrive className="h-4 w-4 mr-2" /> Crear mi primer equipo
                    </Button>
                  </div>
                </div>
              ) : (
                <Button className="bg-[#384091] hover:bg-[#2D3375] text-white" onClick={() => fileRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-2" /> Cargar un nuevo documento
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtrados.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">Sin resultados para tu búsqueda</p>}
            {filtrados.map((x) => (
              <Card key={x.id} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#384091]/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-[#384091]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {TIPO_DOC[x.tipo_documento] || x.tipo_documento}
                      {x.fecha_documento ? ` · ${x.fecha_documento}` : ""}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      Pertenece a: <span className="text-[#0FB4B7] font-medium">{x.equipo}</span>
                      {x.numero_box ? ` (${x.numero_box})` : ""} — {x.descripcion || x.nombre_original}
                    </p>
                  </div>
                  <Select onValueChange={(v) => mover(x, v)}>
                    <SelectTrigger className="h-9 w-9 p-0 justify-center border-gray-200 [&>svg:last-child]:hidden" title="Trasladar a otro equipo">
                      <ArrowRightLeft className="h-4 w-4 text-gray-500" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {equipos.filter((e) => e.id !== x.equipo_id).map((e) => (
                        <SelectItem key={e.id} value={e.id}>→ {etiqueta(e)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => ver(x)}>
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-400 hover:text-red-600" onClick={() => eliminar(x)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
